/**
 * Superadmin console — orgs + users + audit log.
 *
 * All data reads/writes go through /api/superadmin/* endpoints, which are
 * gated server-side by require_superadmin (orka_owner AND email in
 * SUPERADMIN_ALLOWLIST). The client reflects state; it does not enforce.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Shield, Users, Building2, ListTree, Plus, Loader2, Archive, Save, SlidersHorizontal, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

async function api(path, opts = {}) {
  const res = await fetch(`${API}/api/superadmin${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
  return res.json();
}

export default function SuperAdminConsolePage({ me }) {
  const [tab, setTab] = useState('orgs');

  return (
    <div className="min-h-screen orka-surface-page" data-testid="superadmin-console">
      <header className="border-b orka-border-default orka-surface-raised">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg orka-bg-intent-soft flex items-center justify-center">
              <Shield className="w-4 h-4" style={{ color: 'var(--orka-text-intent)' }} />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider orka-text-tertiary">Orka · Platform admin</div>
              <div className="text-base font-semibold orka-text-primary">Superadmin Console</div>
            </div>
          </div>
          <div className="text-xs orka-text-secondary">
            <span className="orka-text-tertiary">signed in as</span> <strong className="orka-text-primary">{me?.email}</strong>
          </div>
        </div>
        <nav className="max-w-7xl mx-auto px-6 flex items-center gap-1">
          <TabButton active={tab === 'orgs'} onClick={() => setTab('orgs')} icon={Building2} label="Organizations" testId="sa-tab-orgs" />
          <TabButton active={tab === 'types'} onClick={() => setTab('types')} icon={SlidersHorizontal} label="Account types" testId="sa-tab-types" />
          <TabButton active={tab === 'users'} onClick={() => setTab('users')} icon={Users} label="Users" testId="sa-tab-users" />
          <TabButton active={tab === 'audit'} onClick={() => setTab('audit')} icon={ListTree} label="Audit log" testId="sa-tab-audit" />
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {tab === 'orgs' && <OrgsTab />}
        {tab === 'types' && <AccountTypesTab />}
        {tab === 'users' && <UsersTab />}
        {tab === 'audit' && <AuditTab />}
      </main>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-2.5 text-sm font-medium flex items-center gap-2 border-b-2 orka-transition-colors ${
        active ? 'orka-text-primary' : 'orka-text-tertiary hover:orka-text-secondary'
      }`}
      style={{ borderColor: active ? 'var(--orka-border-focus)' : 'transparent' }}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

/* ─────────────────────────────── ORGS TAB ────────────────────────────── */
function OrgsTab() {
  const [orgs, setOrgs] = useState([]);
  const [orgTypes, setOrgTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [orgsRes, typesRes] = await Promise.all([
        api(`/orgs${includeArchived ? '?include_archived=true' : ''}`),
        api('/org-types'),
      ]);
      setOrgs(orgsRes.orgs || []);
      setOrgTypes(typesRes.org_types || []);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [includeArchived]);

  useEffect(() => { load(); }, [load]);

  const updateType = async (org, nextType) => {
    try {
      const updated = await api(`/orgs/${org.org_id}`, {
        method: 'PATCH',
        body: JSON.stringify({ org_type: nextType }),
      });
      setOrgs((prev) => prev.map((o) => (o.org_id === org.org_id ? updated : o)));
    } catch (e) { setError(String(e.message || e)); }
  };

  const archiveOrg = async (org) => {
    if (!window.confirm(`Archive org "${org.name}"? Soft delete — can be reversed by changing type.`)) return;
    try {
      await api(`/orgs/${org.org_id}`, { method: 'DELETE' });
      await load();
    } catch (e) { setError(String(e.message || e)); }
  };

  return (
    <section data-testid="sa-orgs">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold orka-text-primary">Organizations</h2>
          <p className="text-xs orka-text-tertiary mt-0.5">{orgs.length} orgs · platform-admin scope only</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs orka-text-secondary">
            <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} data-testid="sa-orgs-include-archived" />
            show archived
          </label>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            data-testid="sa-orgs-create-btn"
            className="px-3 py-1.5 rounded-lg orka-bg-intent text-white text-sm font-medium flex items-center gap-1.5 hover:opacity-90 orka-transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Create org
          </button>
        </div>
      </div>

      {error && <ErrorBanner message={error} testId="sa-orgs-error" />}
      {loading && <LoadingRow />}

      {!loading && (
        <div className="rounded-xl border orka-border-default orka-surface-raised overflow-hidden">
          <table className="w-full text-sm">
            <thead className="orka-surface-subtle text-[11px] uppercase tracking-wider orka-text-tertiary">
              <tr>
                <Th>Name</Th>
                <Th>Slug</Th>
                <Th>Type</Th>
                <Th>Owner user</Th>
                <Th>Created</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {orgs.map((o) => (
                <tr key={o.org_id} className="border-t orka-border-subtle hover:orka-surface-selected" data-testid={`sa-org-row-${o.org_id}`}>
                  <Td><span className="font-medium orka-text-primary">{o.name}</span></Td>
                  <Td><code className="font-mono text-xs orka-text-secondary">{o.slug}</code></Td>
                  <Td>
                    <select
                      value={o.org_type || 'standard_work'}
                      onChange={(e) => updateType(o, e.target.value)}
                      data-testid={`sa-org-type-${o.org_id}`}
                      className="px-2 py-1 rounded-md border orka-border-default orka-surface-base text-xs"
                    >
                      {orgTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Td>
                  <Td><code className="font-mono text-[11px] orka-text-tertiary">{o.owner_user_id}</code></Td>
                  <Td><span className="text-xs orka-text-tertiary">{(o.created_at || '').slice(0, 10)}</span></Td>
                  <Td>
                    <button
                      type="button"
                      onClick={() => archiveOrg(o)}
                      data-testid={`sa-org-archive-${o.org_id}`}
                      className="text-xs px-2 py-1 rounded orka-text-secondary hover:orka-bg-danger-soft flex items-center gap-1"
                    >
                      <Archive className="w-3 h-3" /> Archive
                    </button>
                  </Td>
                </tr>
              ))}
              {orgs.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center orka-text-tertiary text-sm">No organizations.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateOrgModal
          orgTypes={orgTypes}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </section>
  );
}

function CreateOrgModal({ orgTypes, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', slug: '', owner_user_id: '', org_type: 'standard_work' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true); setError('');
    try {
      await api('/orgs', { method: 'POST', body: JSON.stringify(form) });
      onCreated();
    } catch (e) { setError(String(e.message || e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 orka-surface-overlay z-50 flex items-center justify-center p-4" role="dialog" data-testid="sa-create-org-modal">
      <div className="w-full max-w-md orka-surface-raised rounded-xl border orka-border-default p-5 shadow-[var(--orka-shadow-lg)]">
        <h3 className="text-base font-semibold orka-text-primary mb-4">Create organization</h3>
        {error && <ErrorBanner message={error} testId="sa-create-org-error" />}
        <div className="space-y-3">
          <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="sa-create-org-name" />
          <Field label="Slug" placeholder="auto from name" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testId="sa-create-org-slug" />
          <Field label="Owner user_id" value={form.owner_user_id} onChange={(v) => setForm({ ...form, owner_user_id: v })} testId="sa-create-org-owner" />
          <label className="block">
            <span className="block text-xs font-medium orka-text-secondary mb-1">Type</span>
            <select
              value={form.org_type}
              onChange={(e) => setForm({ ...form, org_type: e.target.value })}
              data-testid="sa-create-org-type"
              className="w-full px-3 py-2 rounded-lg border orka-border-default orka-surface-base text-sm"
            >
              {orgTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm orka-text-secondary" data-testid="sa-create-org-cancel">Cancel</button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !form.name || !form.owner_user_id}
            data-testid="sa-create-org-save"
            className="px-3 py-2 rounded-lg orka-bg-intent text-white text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────── ACCOUNT TYPES TAB ────────────────────────── */
function AccountTypesTab() {
  const [packages, setPackages] = useState([]);
  const [catalog, setCatalog] = useState({ modules: [], features: [], privileges: [], billing_modes: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openType, setOpenType] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [pkgRes, catRes] = await Promise.all([
        api('/grant-packages'),
        api('/features-catalog'),
      ]);
      setPackages(pkgRes.packages || []);
      setCatalog({
        modules: catRes.modules || [],
        features: catRes.features || [],
        privileges: catRes.privileges || [],
        billing_modes: catRes.billing_modes || ['free', 'trial', 'recurring', 'manual'],
      });
    } catch (e) { setError(String(e.message || e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const groupedFeatures = useMemo(() => {
    const out = {};
    for (const f of catalog.features) {
      out[f.group] = out[f.group] || [];
      out[f.group].push(f);
    }
    return out;
  }, [catalog.features]);

  const groupedModules = useMemo(() => {
    const out = {};
    for (const m of catalog.modules) {
      out[m.group] = out[m.group] || [];
      out[m.group].push(m);
    }
    return out;
  }, [catalog.modules]);

  const savePackage = async (orgType, patch) => {
    try {
      const updated = await api(`/grant-packages/${orgType}`, {
        method: 'PUT',
        body: JSON.stringify(patch),
      });
      setPackages((prev) => prev.map((p) => (p.org_type === orgType ? updated : p)));
    } catch (e) { setError(String(e.message || e)); }
  };

  return (
    <section data-testid="sa-account-types">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold orka-text-primary">Account types</h2>
          <p className="text-xs orka-text-tertiary mt-0.5">
            Tier-1 grant packages — define features &amp; privileges per <code className="font-mono">org_type</code>. Org admins (tier 2)
            can only hand out a subset of what the package exposes.
          </p>
        </div>
      </div>

      {error && <ErrorBanner message={error} testId="sa-types-error" />}
      {loading && <LoadingRow />}

      {!loading && (
        <div className="space-y-3">
          {packages.map((pkg) => {
            const isOpen = openType === pkg.org_type;
            const moduleCount = Object.values(pkg.modules || {}).filter(Boolean).length;
            const featCount = Object.values(pkg.features || {}).filter(Boolean).length;
            const totalFeatures = catalog.features.length;
            const totalModules = catalog.modules.length;
            return (
              <div
                key={pkg.org_type}
                className="rounded-xl border orka-border-default orka-surface-raised overflow-hidden"
                data-testid={`sa-type-row-${pkg.org_type}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenType(isOpen ? null : pkg.org_type)}
                  className="w-full px-4 py-3 flex items-center gap-3 orka-transition-colors hover:orka-surface-selected"
                  data-testid={`sa-type-toggle-${pkg.org_type}`}
                >
                  <div
                    className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${pkg.org_type === 'super_admin' ? 'orka-priority-fixed' : pkg.org_type === 'founding' ? 'orka-priority-emergency-soft' : pkg.org_type === 'archived' ? 'orka-state-archived-soft' : 'orka-state-open-soft'}`}
                    style={pkg.org_type === 'super_admin' ? { color: 'var(--orka-text-inverse)' } : undefined}
                  >
                    {pkg.org_type}
                  </div>
                  {pkg.org_type === 'super_admin' && (
                    <span
                      className="text-[9px] font-bold uppercase tracking-wider orka-state-blocked-soft px-1.5 py-0.5 rounded"
                      data-testid={`sa-type-superadmin-badge-${pkg.org_type}`}
                    >
                      internal · self-edit
                    </span>
                  )}
                  {(pkg.org_type.startsWith('personal_') || pkg.org_type === 'standard_personal') && (
                    <span
                      className="text-[9px] font-bold uppercase tracking-wider orka-priority-fixed-soft px-1.5 py-0.5 rounded"
                      data-testid={`sa-type-personal-badge-${pkg.org_type}`}
                    >
                      personal
                    </span>
                  )}
                  <div className="text-xs orka-text-secondary flex-1 text-left truncate">{pkg.notes}</div>
                  <div className="text-[11px] orka-text-tertiary">
                    <strong className="orka-text-secondary">{moduleCount}/{totalModules} modules</strong> · {featCount}/{totalFeatures} features · {(pkg.privileges || []).length}/{catalog.privileges.length} privileges
                    {pkg.billing && <> · <BillingSummary b={pkg.billing} /></>}
                  </div>
                  <span className="text-xs orka-text-tertiary">{isOpen ? '−' : '+'}</span>
                </button>

                {isOpen && (
                  <PackageEditor
                    pkg={pkg}
                    groupedModules={groupedModules}
                    groupedFeatures={groupedFeatures}
                    privilegeCatalog={catalog.privileges}
                    billingModes={catalog.billing_modes}
                    onSave={(patch) => savePackage(pkg.org_type, patch)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function PackageEditor({ pkg, groupedModules, groupedFeatures, privilegeCatalog, onSave, billingModes }) {
  const [modules, setModules] = useState(pkg.modules || {});
  const [features, setFeatures] = useState(pkg.features || {});
  const [privileges, setPrivileges] = useState(new Set(pkg.privileges || []));
  const [notes, setNotes] = useState(pkg.notes || '');
  const [billing, setBilling] = useState(() => JSON.parse(JSON.stringify(pkg.billing || {
    mode: 'free', currency: 'usd',
    monthly_price_cents: 0, yearly_price_cents: 0,
    trial_days: 0, free_period_days: 0,
    seat_based: false, per_seat_price_cents: 0,
    default_cadence: 'monthly', cancel_at_period_end_default: false,
    stripe: { product_id: '', price_id_monthly: '', price_id_yearly: '', price_id_per_seat: '', trial_price_id: '', tax_rate_id: '' },
  })));
  const [saving, setSaving] = useState(false);

  const dirty = useMemo(() => {
    if (notes !== (pkg.notes || '')) return true;
    if (JSON.stringify(billing) !== JSON.stringify(pkg.billing || {})) return true;
    const origMods = pkg.modules || {};
    for (const k of Object.keys({ ...origMods, ...modules })) {
      if (Boolean(origMods[k]) !== Boolean(modules[k])) return true;
    }
    const origFeats = pkg.features || {};
    for (const k of Object.keys({ ...origFeats, ...features })) {
      if (Boolean(origFeats[k]) !== Boolean(features[k])) return true;
    }
    const origPrivs = new Set(pkg.privileges || []);
    if (origPrivs.size !== privileges.size) return true;
    for (const p of origPrivs) if (!privileges.has(p)) return true;
    return false;
  }, [pkg, modules, features, privileges, notes, billing]);

  const toggleModule = (key) => setModules((m) => ({ ...m, [key]: !m[key] }));
  const toggleFeature = (key) => setFeatures((f) => ({ ...f, [key]: !f[key] }));
  const togglePriv = (key) => setPrivileges((p) => {
    const n = new Set(p); if (n.has(key)) n.delete(key); else n.add(key); return n;
  });
  const setB = (k, v) => setBilling((b) => ({ ...b, [k]: v }));
  const setStripe = (k, v) => setBilling((b) => ({ ...b, stripe: { ...(b.stripe || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ modules, features, privileges: Array.from(privileges), notes, billing });
    } finally { setSaving(false); }
  };

  return (
    <div className="border-t orka-border-subtle p-4 orka-surface-subtle" data-testid={`sa-type-editor-${pkg.org_type}`}>

      {/* Modules — PRIMARY editor. Controls which items appear in the nav. */}
      <div className="mb-5 rounded-lg border orka-border-default orka-surface-raised p-4" data-testid={`sa-modules-${pkg.org_type}`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider orka-text-primary">Menu modules</h4>
            <div className="text-[11px] orka-text-tertiary mt-0.5">
              Which top-level items appear in the left navigation for users on this account type.
            </div>
          </div>
        </div>
        <div className="space-y-2">
          {Object.entries(groupedModules).map(([group, mods]) => (
            <div key={group} className="rounded border orka-border-subtle orka-surface-subtle p-2.5">
              <div className="text-[10px] font-semibold orka-text-secondary mb-1.5 uppercase tracking-wider">{group}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
                {mods.map((m) => (
                  <label key={m.key} className="flex items-center gap-2 text-sm cursor-pointer px-1.5 py-1 rounded hover:orka-surface-selected orka-transition-colors">
                    <input
                      type="checkbox"
                      checked={Boolean(modules[m.key])}
                      onChange={() => toggleModule(m.key)}
                      data-testid={`sa-module-${pkg.org_type}-${m.key}`}
                    />
                    <span className={modules[m.key] ? 'orka-text-primary font-medium' : 'orka-text-tertiary'}>{m.label}</span>
                    <code className="ml-auto font-mono text-[10px] orka-text-tertiary">{m.key}</code>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-5">
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider orka-text-tertiary mb-2">Features</h4>
          <div className="space-y-3">
            {Object.entries(groupedFeatures).map(([group, feats]) => (
              <div key={group} className="rounded-lg border orka-border-subtle orka-surface-raised p-3">
                <div className="text-[11px] font-semibold orka-text-secondary mb-2 uppercase tracking-wider">{group}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                  {feats.map((f) => (
                    <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer px-1.5 py-1 rounded hover:orka-surface-selected orka-transition-colors">
                      <input
                        type="checkbox"
                        checked={Boolean(features[f.key])}
                        onChange={() => toggleFeature(f.key)}
                        data-testid={`sa-feature-${pkg.org_type}-${f.key}`}
                      />
                      <span className={features[f.key] ? 'orka-text-primary' : 'orka-text-tertiary'}>{f.label}</span>
                      <code className="ml-auto font-mono text-[10px] orka-text-tertiary">{f.key}</code>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider orka-text-tertiary mb-2">Privileges</h4>
          <div className="rounded-lg border orka-border-subtle orka-surface-raised p-3 space-y-1.5">
            {privilegeCatalog.map((p) => (
              <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer px-1.5 py-1 rounded hover:orka-surface-selected orka-transition-colors">
                <input
                  type="checkbox"
                  checked={privileges.has(p.key)}
                  onChange={() => togglePriv(p.key)}
                  data-testid={`sa-priv-${pkg.org_type}-${p.key}`}
                />
                <span className={privileges.has(p.key) ? 'orka-text-primary' : 'orka-text-tertiary'}>{p.label}</span>
                <code className="ml-auto font-mono text-[10px] orka-text-tertiary">{p.key}</code>
              </label>
            ))}
          </div>

          <h4 className="text-xs font-bold uppercase tracking-wider orka-text-tertiary mt-5 mb-2">Notes</h4>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            data-testid={`sa-notes-${pkg.org_type}`}
            className="w-full px-3 py-2 rounded-lg border orka-border-default orka-surface-base text-sm"
            placeholder="Internal commentary (never shown to org admins)"
          />
        </div>
      </div>

      {/* Billing section — spans full width below the features/privileges grid */}
      <div className="mt-5 rounded-lg border orka-border-subtle orka-surface-raised p-4" data-testid={`sa-billing-${pkg.org_type}`}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-bold uppercase tracking-wider orka-text-tertiary">Billing &amp; Stripe</h4>
          <span className="text-[10px] orka-text-tertiary">money fields are integer cents · currency ISO-4217</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <BField label="Mode">
            <select
              value={billing.mode || 'free'}
              onChange={(e) => setB('mode', e.target.value)}
              data-testid={`sa-billing-mode-${pkg.org_type}`}
              className="w-full px-2 py-1.5 rounded border orka-border-default orka-surface-base text-sm"
            >
              {billingModes.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </BField>
          <BField label="Currency">
            <input
              type="text"
              value={billing.currency || 'usd'}
              onChange={(e) => setB('currency', e.target.value.toLowerCase())}
              data-testid={`sa-billing-currency-${pkg.org_type}`}
              className="w-full px-2 py-1.5 rounded border orka-border-default orka-surface-base text-sm"
            />
          </BField>
          <BField label="Default cadence">
            <select
              value={billing.default_cadence || 'monthly'}
              onChange={(e) => setB('default_cadence', e.target.value)}
              data-testid={`sa-billing-cadence-${pkg.org_type}`}
              className="w-full px-2 py-1.5 rounded border orka-border-default orka-surface-base text-sm"
            >
              <option value="monthly">monthly</option>
              <option value="yearly">yearly</option>
            </select>
          </BField>
          <BField label="Trial days">
            <input
              type="number" min="0" max="365"
              value={billing.trial_days ?? 0}
              onChange={(e) => setB('trial_days', Number(e.target.value))}
              data-testid={`sa-billing-trial-${pkg.org_type}`}
              className="w-full px-2 py-1.5 rounded border orka-border-default orka-surface-base text-sm"
            />
          </BField>
          <BField label="Monthly (cents)">
            <input
              type="number" min="0"
              value={billing.monthly_price_cents ?? 0}
              onChange={(e) => setB('monthly_price_cents', Number(e.target.value))}
              data-testid={`sa-billing-monthly-${pkg.org_type}`}
              className="w-full px-2 py-1.5 rounded border orka-border-default orka-surface-base text-sm"
            />
          </BField>
          <BField label="Yearly (cents)">
            <input
              type="number" min="0"
              value={billing.yearly_price_cents ?? 0}
              onChange={(e) => setB('yearly_price_cents', Number(e.target.value))}
              data-testid={`sa-billing-yearly-${pkg.org_type}`}
              className="w-full px-2 py-1.5 rounded border orka-border-default orka-surface-base text-sm"
            />
          </BField>
          <BField label="Free period days (grace)">
            <input
              type="number" min="0" max="365"
              value={billing.free_period_days ?? 0}
              onChange={(e) => setB('free_period_days', Number(e.target.value))}
              data-testid={`sa-billing-freeperiod-${pkg.org_type}`}
              className="w-full px-2 py-1.5 rounded border orka-border-default orka-surface-base text-sm"
            />
          </BField>
          <BField label="Seat-based">
            <label className="flex items-center gap-2 h-[34px] text-sm">
              <input
                type="checkbox"
                checked={Boolean(billing.seat_based)}
                onChange={(e) => setB('seat_based', e.target.checked)}
                data-testid={`sa-billing-seatbased-${pkg.org_type}`}
              />
              <span className="orka-text-secondary">seats multiplied</span>
            </label>
          </BField>
          {billing.seat_based && (
            <BField label="Per seat (cents / month)">
              <input
                type="number" min="0"
                value={billing.per_seat_price_cents ?? 0}
                onChange={(e) => setB('per_seat_price_cents', Number(e.target.value))}
                data-testid={`sa-billing-seatprice-${pkg.org_type}`}
                className="w-full px-2 py-1.5 rounded border orka-border-default orka-surface-base text-sm"
              />
            </BField>
          )}
          <BField label="Cancel at period end (default)">
            <label className="flex items-center gap-2 h-[34px] text-sm">
              <input
                type="checkbox"
                checked={Boolean(billing.cancel_at_period_end_default)}
                onChange={(e) => setB('cancel_at_period_end_default', e.target.checked)}
                data-testid={`sa-billing-cape-${pkg.org_type}`}
              />
              <span className="orka-text-secondary">auto-cancel at renewal</span>
            </label>
          </BField>
        </div>

        {/* Stripe IDs — paste straight from dashboard; never shown to end users */}
        <div className="mt-4 pt-3 border-t orka-border-subtle">
          <div className="text-[11px] font-semibold orka-text-secondary mb-2 uppercase tracking-wider">Stripe IDs (dashboard copy-paste — superadmin only)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <BField label="product_id">
              <input type="text" value={billing.stripe?.product_id || ''} onChange={(e) => setStripe('product_id', e.target.value)} data-testid={`sa-stripe-product-${pkg.org_type}`} placeholder="prod_…" className="w-full px-2 py-1.5 rounded border orka-border-default orka-surface-base text-xs font-mono" />
            </BField>
            <BField label="price_id_monthly">
              <input type="text" value={billing.stripe?.price_id_monthly || ''} onChange={(e) => setStripe('price_id_monthly', e.target.value)} data-testid={`sa-stripe-price-mo-${pkg.org_type}`} placeholder="price_…" className="w-full px-2 py-1.5 rounded border orka-border-default orka-surface-base text-xs font-mono" />
            </BField>
            <BField label="price_id_yearly">
              <input type="text" value={billing.stripe?.price_id_yearly || ''} onChange={(e) => setStripe('price_id_yearly', e.target.value)} data-testid={`sa-stripe-price-yr-${pkg.org_type}`} placeholder="price_…" className="w-full px-2 py-1.5 rounded border orka-border-default orka-surface-base text-xs font-mono" />
            </BField>
            <BField label="price_id_per_seat">
              <input type="text" value={billing.stripe?.price_id_per_seat || ''} onChange={(e) => setStripe('price_id_per_seat', e.target.value)} data-testid={`sa-stripe-price-seat-${pkg.org_type}`} placeholder="price_…" className="w-full px-2 py-1.5 rounded border orka-border-default orka-surface-base text-xs font-mono" />
            </BField>
            <BField label="trial_price_id">
              <input type="text" value={billing.stripe?.trial_price_id || ''} onChange={(e) => setStripe('trial_price_id', e.target.value)} data-testid={`sa-stripe-trial-${pkg.org_type}`} placeholder="price_…" className="w-full px-2 py-1.5 rounded border orka-border-default orka-surface-base text-xs font-mono" />
            </BField>
            <BField label="tax_rate_id">
              <input type="text" value={billing.stripe?.tax_rate_id || ''} onChange={(e) => setStripe('tax_rate_id', e.target.value)} data-testid={`sa-stripe-tax-${pkg.org_type}`} placeholder="txr_…" className="w-full px-2 py-1.5 rounded border orka-border-default orka-surface-base text-xs font-mono" />
            </BField>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-4 pt-3 border-t orka-border-subtle">
        <div className="text-[11px] orka-text-tertiary">
          {pkg.updated_at ? (
            <>Last edit <span className="font-mono">{pkg.updated_at.slice(0, 19).replace('T', ' ')}</span> by {pkg.updated_by_email}</>
          ) : 'Never edited.'}
        </div>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          data-testid={`sa-save-${pkg.org_type}`}
          className="px-3 py-1.5 rounded-lg orka-bg-intent text-white text-xs font-medium flex items-center gap-1.5 disabled:opacity-40"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : dirty ? <Save className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
          {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
        </button>
      </div>
    </div>
  );
}

function BField({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-semibold uppercase tracking-wider orka-text-tertiary mb-1">{label}</span>
      {children}
    </label>
  );
}

function BillingSummary({ b }) {
  if (!b) return null;
  const cur = (b.currency || 'usd').toUpperCase();
  const fmt = (cents) => `${cur} ${(cents / 100).toFixed(2)}`;
  if (b.mode === 'free') return <span data-testid="sa-billing-summary">free</span>;
  if (b.mode === 'manual') return <span data-testid="sa-billing-summary">manual invoice</span>;
  if (b.mode === 'trial') return <span data-testid="sa-billing-summary">{b.trial_days || 0}-day trial</span>;
  // recurring
  const parts = [];
  if (b.seat_based && b.per_seat_price_cents) parts.push(`${fmt(b.per_seat_price_cents)}/seat/mo`);
  else {
    if (b.monthly_price_cents) parts.push(`${fmt(b.monthly_price_cents)}/mo`);
    if (b.yearly_price_cents)  parts.push(`${fmt(b.yearly_price_cents)}/yr`);
  }
  if (b.trial_days) parts.push(`${b.trial_days}d trial`);
  return <span data-testid="sa-billing-summary">{parts.join(' · ') || 'recurring'}</span>;
}

/* ─────────────────────────────── USERS TAB ───────────────────────────── */
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [personalTypes, setPersonalTypes] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await api(`/users${q ? `?q=${encodeURIComponent(q)}` : ''}`);
      setUsers(res.users || []);
      setPersonalTypes(res.personal_account_types || []);
    } catch (e) { setError(String(e.message || e)); }
    finally { setLoading(false); }
  }, [q]);

  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);

  const setUserType = async (user, nextType) => {
    try {
      const updated = await api(`/users/${user.user_id}/personal-account-type`, {
        method: 'PATCH',
        body: JSON.stringify({ personal_account_type: nextType }),
      });
      setUsers((prev) => prev.map((u) => (u.user_id === user.user_id ? updated : u)));
    } catch (e) { setError(String(e.message || e)); }
  };

  return (
    <section data-testid="sa-users">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold orka-text-primary">Users</h2>
          <p className="text-xs orka-text-tertiary mt-0.5">
            {users.length} results · personal account type applies to each user's personal workspace (independent of org memberships).
          </p>
        </div>
        <input
          placeholder="Search name or email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="sa-users-search"
          className="px-3 py-2 rounded-lg border orka-border-default orka-surface-base text-sm w-72"
        />
      </div>
      {error && <ErrorBanner message={error} testId="sa-users-error" />}
      {loading && <LoadingRow />}
      {!loading && (
        <div className="rounded-xl border orka-border-default orka-surface-raised overflow-hidden">
          <table className="w-full text-sm">
            <thead className="orka-surface-subtle text-[11px] uppercase tracking-wider orka-text-tertiary">
              <tr><Th>Email</Th><Th>Name</Th><Th>Personal account type</Th><Th>user_id</Th><Th>Created</Th></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-t orka-border-subtle" data-testid={`sa-user-row-${u.user_id}`}>
                  <Td><span className="orka-text-primary">{u.email}</span></Td>
                  <Td>{u.name || <span className="orka-text-tertiary">—</span>}</Td>
                  <Td>
                    <select
                      value={u.personal_account_type || 'personal_free'}
                      onChange={(e) => setUserType(u, e.target.value)}
                      data-testid={`sa-user-personal-type-${u.user_id}`}
                      className="px-2 py-1 rounded-md border orka-border-default orka-surface-base text-xs"
                    >
                      {personalTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Td>
                  <Td><code className="font-mono text-[11px] orka-text-tertiary">{u.user_id}</code></Td>
                  <Td><span className="text-xs orka-text-tertiary">{(u.created_at || '').slice(0, 10)}</span></Td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={5} className="py-8 text-center orka-text-tertiary text-sm">No users.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* ─────────────────────────────── AUDIT TAB ───────────────────────────── */
function AuditTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    (async () => {
      try { const r = await api('/audit'); setEntries(r.entries || []); }
      catch (e) { setError(String(e.message || e)); }
      finally { setLoading(false); }
    })();
  }, []);
  return (
    <section data-testid="sa-audit">
      <h2 className="text-xl font-semibold orka-text-primary mb-4">Audit log <span className="text-xs orka-text-tertiary font-normal">({entries.length} entries · append-only)</span></h2>
      {error && <ErrorBanner message={error} testId="sa-audit-error" />}
      {loading && <LoadingRow />}
      {!loading && (
        <div className="rounded-xl border orka-border-default orka-surface-raised overflow-hidden">
          <table className="w-full text-sm">
            <thead className="orka-surface-subtle text-[11px] uppercase tracking-wider orka-text-tertiary">
              <tr><Th>When</Th><Th>Email</Th><Th>Action</Th><Th>Allowed</Th><Th>Target</Th><Th>Reason / meta</Th></tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.audit_id} className="border-t orka-border-subtle align-top" data-testid={`sa-audit-row-${e.audit_id}`}>
                  <Td><span className="text-[11px] orka-text-tertiary font-mono">{(e.ts || '').replace('T', ' ').slice(0, 19)}</span></Td>
                  <Td><span className="text-xs orka-text-primary">{e.email || '—'}</span></Td>
                  <Td><code className="font-mono text-[11px] orka-text-secondary">{e.action}</code></Td>
                  <Td>
                    <span
                      className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${e.allowed ? 'orka-state-done-soft' : 'orka-state-blocked-soft'}`}
                    >
                      {e.allowed ? 'allowed' : 'denied'}
                    </span>
                  </Td>
                  <Td>{e.target_type ? <span className="text-xs orka-text-secondary">{e.target_type}:{e.target_id}</span> : <span className="orka-text-tertiary">—</span>}</Td>
                  <Td><span className="text-xs orka-text-tertiary">{e.reason || ''}{e.metadata && Object.keys(e.metadata).length ? ` · ${JSON.stringify(e.metadata)}` : ''}</span></Td>
                </tr>
              ))}
              {entries.length === 0 && <tr><td colSpan={6} className="py-8 text-center orka-text-tertiary text-sm">No entries yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* ──────────────────────────── SHARED ATOMS ───────────────────────────── */
function Th({ children }) { return <th className="text-left px-3 py-2.5 font-semibold">{children}</th>; }
function Td({ children }) { return <td className="px-3 py-2.5">{children}</td>; }
function LoadingRow() {
  return (
    <div className="flex items-center gap-2 py-10 justify-center orka-text-tertiary">
      <Loader2 className="w-4 h-4 animate-spin" /> <span className="text-sm">Loading…</span>
    </div>
  );
}
function ErrorBanner({ message, testId }) {
  return (
    <div className="mb-3 p-3 rounded-lg orka-bg-danger-soft text-xs orka-text-primary" data-testid={testId}>
      {message}
    </div>
  );
}
function Field({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium orka-text-secondary mb-1">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full px-3 py-2 rounded-lg border orka-border-default orka-surface-base text-sm"
      />
    </label>
  );
}
